
import { Component, Vue } from 'vue-property-decorator'
import { Customer } from '@/models'
import { useUserStore } from '@/stores/user'
import { useDeveloperModeStore } from '@/stores/developerMode'
import * as XLSX from 'xlsx'

@Component({
  name: 'Customers',
  components: {
    CustomersHeader: () => import('./components/CustomersHeader.vue'),
    CustomersHeaderClassic: () => import('./components/CustomersHeaderClassic.vue'),
    CustomersTable: () => import('./components/CustomersTable.vue'),
    CustomerCard: () => import('./components/CustomerCard.vue'),
    DialogCustomer: () => import('./components/DialogCustomer.vue'),
    DialogImportCustomers: () => import('./components/DialogImportCustomers.vue'),
    FilterDrawer: () => import('./components/FiltersDrawer.vue'),
    DialogManageDomain: () => import('./components/DialogManageDomain.vue')
  }
})
export default class extends Vue {
  userStore: any = useUserStore()
  developerModeStore = useDeveloperModeStore()

  customers: Customer[] = []
  totalCustomers = 0
  customersLoading = true
  exporting = false

  tempCustomer: Customer | null = null
  visibleDialog = false

  listQuery: any = {
    sortByDate: 'desc',
    sortByUpdate: null,
    sortByName: null,
    page: 1,
    firstName: null,
    lastName: null,
    email: null,
    businessEmail: null,
    confirmed: null,
    approval: null,
    registrationFailureReason: null,
    dateRange: null,
    programId: null,
    domain: null,
    zipcode: null,
    isRetired: null,
    lockedOnly: false
  }

  get isCaretakerIEG(): boolean {
    return this.userStore.isCaretakerIEG
  }

  get isDeveloperMode(): boolean {
    return this.developerModeStore.enabled
  }

  created(): void {
    this.getCustomers()
  }

  async getCustomers(): Promise<void> {
    this.customersLoading = true

    const createdAtGte = this.listQuery.dateRange ? this.$moment(String(this.listQuery.dateRange[0])).format('YYYY-MM-DD HH:mm:ss') : ''
    const createdAtLte = this.listQuery.dateRange ? this.$moment(String(this.listQuery.dateRange[1])).format('YYYY-MM-DD HH:mm:ss') : ''
    let sort: any = { createdAt: 'desc' }
    if (this.listQuery.sortByName) {
      sort = { lastname: this.listQuery.sortByName }
    } else if (this.listQuery.sortByUpdate) {
      sort = { updatedAt: this.listQuery.sortByUpdate }
    } else if (this.listQuery.sortByDate) {
      sort = { createdAt: this.listQuery.sortByDate }
    }

    const { data, meta } =
      await Customer
        .includes(['program', 'location'])
        .where({ firstname: { prefix: this.listQuery.firstName } })
        .where({ lastname: { prefix: this.listQuery.lastName } })
        .where({ email: { prefix: this.listQuery.email } })
        .where({ email_domain: { prefix: this.listQuery.domain } })
        .where({ businessEmail: { prefix: this.listQuery.businessEmail } })
        .where({ isConfirmed: this.listQuery.confirmed })
        .where({ registrationStatus: this.listQuery.approval })
        .where({ registrationFailureReason: this.listQuery.registrationFailureReason })
        .where({ createdAt: { gte: createdAtGte, lte: createdAtLte } })
        .where({ programId: this.listQuery.programId })
        .where({ location_zipcode: this.listQuery.zipcode })
        .where({ is_retired: this.listQuery.isRetired })
        .where({ locked: this.listQuery.lockedOnly ? true : null })
        .page(this.listQuery.page)
        .per(20)
        .order(sort)
        .stats({ total: 'count' })
        .all()

    this.customers.push(...data)
    this.totalCustomers = meta.stats.total.count
    this.listQuery.page += 1
    this.customersLoading = false
  }

  setCustomer(customer: Customer): void {
    this.customers.unshift(customer)
    this.totalCustomers += 1
  }

  updateCustomer(customer: Customer): void {
    const index = this.customers.findIndex((c) => c.id === customer.id)
    this.customers.splice(index, 1, customer)
  }

  async getCustomer(customerId: string): Promise<void> {
    const { data } = await Customer.includes(['program']).find(customerId)

    if (data) {
      const index = this.customers.findIndex((c) => c.id === data.id)
      this.customers.splice(index, 1, data)
    }
  }

  deleteCustomer(customer: Customer): void {
    this.customers.splice(this.customers.findIndex((c: Customer) => c.id === customer.id), 1)
    this.totalCustomers = Math.max(0, this.totalCustomers - 1)
  }

  setFilters(filters: any): void {
    this.listQuery = filters || this.listQuery
    this.listQuery.page = 1
    this.totalCustomers = 0
    this.customers.splice(0, this.customers.length)

    this.getCustomers()
  }

  refreshCustomers(): void {
    this.listQuery.page = 1
    this.totalCustomers = 0
    this.customers.splice(0, this.customers.length)
    this.getCustomers()
  }

  async handleExport(): Promise<void> {
    if (this.customers.length === 0) return

    this.exporting = true
    try {
      const allCustomers = await this.fetchAllCustomersForExport()
      const exportData = allCustomers.map(customer => this.mapCustomerForExport(customer))

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Customers')

      const fileName = `customers_export_${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(wb, fileName)

      this.$notify({
        title: this.$t('customers.notification.export.success.title').toString(),
        message: this.$t('customers.notification.export.success.subtitle').toString(),
        type: 'success',
        duration: 2000
      })
    } catch (error) {
      console.error('Export error:', error)
      this.$notify({
        title: this.$t('customers.notification.export.error.title').toString(),
        message: error instanceof Error ? error.message : this.$t('customers.notification.export.error.subtitle').toString(),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.exporting = false
    }
  }

  async handleExportCSV(): Promise<void> {
    this.exporting = true
    try {
      const allCustomers = await this.fetchAllCustomersForExport()
      const exportData = allCustomers.map(customer => this.mapCustomerForExport(customer))

      const headers = Object.keys(exportData[0])
      const csvRows = [
        headers.join(';'),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header] || ''
            return `"${value.toString().replace(/"/g, '""')}"`
          }).join(';')
        )
      ]
      const csv = csvRows.join('\n')

      const BOM = '\uFEFF'
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$notify({
        title: this.$t('customers.notification.export.success.title').toString(),
        message: this.$t('customers.notification.export.success.subtitle').toString(),
        type: 'success',
        duration: 2000
      })
    } catch (error) {
      console.error('Export error:', error)
      this.$notify({
        title: this.$t('customers.notification.export.error.title').toString(),
        message: error instanceof Error ? error.message : this.$t('customers.notification.export.error.subtitle').toString(),
        type: 'error',
        duration: 5000
      })
    } finally {
      this.exporting = false
    }
  }

  private async fetchAllCustomersForExport(): Promise<Customer[]> {
    let allCustomers: Customer[] = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      const createdAtGte = this.listQuery.dateRange ? this.$moment(String(this.listQuery.dateRange[0])).format('YYYY-MM-DD HH:mm:ss') : ''
      const createdAtLte = this.listQuery.dateRange ? this.$moment(String(this.listQuery.dateRange[1])).format('YYYY-MM-DD HH:mm:ss') : ''
      let sort: any = { createdAt: 'desc' }
      if (this.listQuery.sortByName) {
        sort = { lastname: this.listQuery.sortByName }
      } else if (this.listQuery.sortByUpdate) {
        sort = { updatedAt: this.listQuery.sortByUpdate }
      } else if (this.listQuery.sortByDate) {
        sort = { createdAt: this.listQuery.sortByDate }
      }

      const { data } = await Customer
        .includes(['program', 'location'])
        .where({ firstname: { prefix: this.listQuery.firstName } })
        .where({ lastname: { prefix: this.listQuery.lastName } })
        .where({ email: { prefix: this.listQuery.email } })
        .where({ email_domain: { prefix: this.listQuery.domain } })
        .where({ businessEmail: { prefix: this.listQuery.businessEmail } })
        .where({ isConfirmed: this.listQuery.confirmed })
        .where({ registrationStatus: this.listQuery.approval })
        .where({ registrationFailureReason: this.listQuery.registrationFailureReason })
        .where({ createdAt: { gte: createdAtGte, lte: createdAtLte } })
        .where({ programId: this.listQuery.programId })
        .where({ location_zipcode: this.listQuery.zipcode })
        .where({ is_retired: this.listQuery.isRetired })
        .where({ locked: this.listQuery.lockedOnly ? true : null })
        .page(page)
        .per(1000)
        .order(sort)
        .all()

      allCustomers = allCustomers.concat(data)
      hasMore = data.length === 1000
      page++
    }

    return allCustomers
  }

  private mapCustomerForExport(customer: Customer): Record<string, string> {
    if (!customer) {
      throw new Error('Invalid customer data in results')
    }

    const row: Record<string, string> = {}
    row[this.$t('customers.card.firstname').toString()] = customer.firstname || ''
    row[this.$t('customers.card.lastname').toString()] = customer.lastname || ''
    row[this.$t('customers.card.email').toString()] = customer.email || ''
    row[this.$t('customers.card.phone').toString()] = customer.phoneNumber || ''
    row[this.$t('customers.card.zipcode').toString()] = customer.location?.zipcode || ''
    row[this.$t('customers.card.program').toString()] = customer.program?.name || ''
    row[this.$t('customers.card.confirmedShort').toString()] = customer.isConfirmed ? this.$t('customers.card.yes').toString() : this.$t('customers.card.no').toString()
    row[this.$t('customers.card.approved').toString()] = this.getApprovalStatus(customer)
    return row
  }

  private getApprovalStatus(customer: Customer): string {
    if (customer.registrationStatus === 'verified') return this.$t('customers.card.yes').toString()
    if (customer.registrationStatus === 'waiting') return this.$t('customers.card.waiting').toString()
    if (customer.registrationStatus === 'verification_failed') return this.$t('customers.card.no').toString()
    return this.$t('customers.card.notSpecified').toString()
  }
}
